import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AppProvider } from '@shopify/polaris';
import { useState, useEffect } from 'react';

import enTranslations from '@shopify/polaris/locales/en.json';
import '@shopify/polaris/build/esm/styles.css';
import './katana-colors.css';

// Components
import AppLayout from '@/components/Layout/AppLayout';
import Dashboard from '@/components/Dashboard/Dashboard';
import PurchaseOrdersList from '@/components/PurchaseOrders/PurchaseOrdersList';
import PurchaseOrderDetail from '@/components/PurchaseOrders/PurchaseOrderDetail';
import OutsourcedPOList from '@/components/PurchaseOrders/OutsourcedPOList';
import ManufacturingOrdersList from '@/components/ManufacturingOrders/ManufacturingOrdersList';
import KatanaSettings from '@/components/Settings/KatanaSettings';
import SimpleInventoryAnalytics from '@/components/InventoryAnalytics/SimpleInventoryAnalytics';

function App() {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Ensure proper initialization order
    const initializeApp = () => {
      // Small delay to ensure CSS is loaded
      setTimeout(() => {
        setIsInitialized(true);
      }, 100);
    };

    // Initialize immediately if document is ready
    if (document.readyState === 'complete') {
      initializeApp();
    } else {
      // Wait for document to be ready
      document.addEventListener('DOMContentLoaded', initializeApp);
      window.addEventListener('load', initializeApp);
    }

    return () => {
      document.removeEventListener('DOMContentLoaded', initializeApp);
      window.removeEventListener('load', initializeApp);
    };
  }, []);

  // Show minimal loading state while initializing
  if (!isInitialized) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f6f6f7',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}>
        <div style={{
          textAlign: 'center',
          color: '#202223',
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            border: '3px solid #e1e3e5',
            borderTop: '3px solid #008060',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px',
          }} />
          <div>Loading...</div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <AppProvider 
      i18n={enTranslations}
      features={{
        newDesignLanguage: true,
      }}
    >
      <Router>
        <AppLayout>
          <Routes>
            {/* Dashboard Overview */}
            <Route path="/" element={<Dashboard />} />
            
            {/* Inventory Analytics */}
            <Route path="/inventory-analytics" element={<SimpleInventoryAnalytics />} />
            
            {/* Purchase Orders */}
            <Route path="/purchase-orders" element={<PurchaseOrdersList />} />
            <Route path="/purchase-orders/:id" element={<PurchaseOrderDetail />} />
            
            {/* Outsourced Purchase Orders */}
            <Route path="/outsourced-orders" element={<OutsourcedPOList />} />
            
            {/* Manufacturing Orders */}
            <Route path="/manufacturing-orders" element={<ManufacturingOrdersList />} />
            
            {/* Settings */}
            <Route path="/settings" element={<KatanaSettings />} />
          </Routes>
        </AppLayout>
      </Router>
    </AppProvider>
  );
}

export default App;
